import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { FilterFormLoading, PanelLoading } from "~/components/loading";
import { ShareButton } from "~/components/share-button";
import type { ViewState } from "~/components/timeline";

import type { RelationListFilters, RelationSortOrder, RelationView, SortDirection } from "~/features/relations/types";
import { RelationPeek } from "./RelationPeek";
import { RelationsSearchForm } from "./RelationsSearchForm";
import { RelationsTable } from "./RelationsTable";
import { RelationsTimelineView } from "./RelationsTimelineView";
import { RelationsViewSwitcher } from "./RelationsViewSwitcher";

interface RelationsPresenterProps {
  view: RelationView;
  onViewChange: (next: RelationView) => void;
  filters: RelationListFilters;
  onApplyFilters: (next: RelationListFilters) => void;
  sortOrder: RelationSortOrder;
  direction: SortDirection;
  onSortingChange: (order: RelationSortOrder, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
  onAddCategory: (categoryId: string) => void;
  selectedRelation: string | null;
  onCloseSelected: () => void;
  initialTimelineViewState: ViewState | undefined;
  onTimelineViewStateChange: (centerYear: number, scale: number) => void;
}

export function RelationsPresenter({
  view,
  onViewChange,
  filters,
  onApplyFilters,
  sortOrder,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
  onAddCategory,
  selectedRelation,
  onCloseSelected,
  initialTimelineViewState,
  onTimelineViewStateChange,
}: RelationsPresenterProps) {
  const { t } = useTranslation();

  // Filter/search changes navigate (RR wraps it in a transition), so an in-place re-suspense
  // would keep the stale table instead of showing the fallback. Keying the Suspense boundary by
  // the filter signature remounts it, forcing the shimmer. Sort/paging keep the key stable and
  // fall through to the table's dimmed-rows pending state.
  const filterKey = JSON.stringify([
    filters.idOrName,
    filters.categoryIds,
    filters.referenceIds,
    filters.excludeDeactivated,
    filters.gdtfFrom,
    filters.gdtfTo,
  ]);

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex items-center justify-between">
        <h1 className="text-dewey-title capitalize text-fg-1">{t("model.record.relation.modelNames")}</h1>
        <div className="flex items-center gap-2">
          <ShareButton />
          <RelationsViewSwitcher value={view} onChange={onViewChange} />
        </div>
      </div>

      <div className="grid grid-cols-[240px_minmax(0,1fr)] gap-6">
        <aside className="min-w-0 overflow-hidden border-r border-border-default pr-4">
          <Suspense fallback={<FilterFormLoading />}>
            <RelationsSearchForm filters={filters} onApply={onApplyFilters} />
          </Suspense>
        </aside>

        <main className="min-w-0">
          {view === "table" && (
            <Suspense key={filterKey} fallback={<PanelLoading />}>
              <RelationsTable
                filters={filters}
                sortOrder={sortOrder}
                direction={direction}
                onSortingChange={onSortingChange}
                pageIndex={pageIndex}
                onPageIndexChange={onPageIndexChange}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
                isPending={isPending}
                onSelect={onSelect}
                onAddCategory={onAddCategory}
              />
            </Suspense>
          )}
          {view === "timeline" && (
            <Suspense fallback={<PanelLoading />}>
              <RelationsTimelineView
                filters={filters}
                onSelect={onSelect}
                initialViewState={initialTimelineViewState}
                onViewStateChange={onTimelineViewStateChange}
              />
            </Suspense>
          )}
        </main>
      </div>

      <RelationPeek id={selectedRelation} onClose={onCloseSelected} />
    </div>
  );
}
